import { ChevronUpIcon } from '@heroicons/react/24/outline'
import { useWindowScroll } from '@mantine/hooks'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import EarlyKlassTooltip from '../../components/EarlyKlassTooltip'
import isEmpty from 'lodash/isEmpty'
import { AdditionalList } from '../../_const/kdt'
import { FC, useMemo, useRef, useState } from 'react'

import BasicTimer from '../../components/KlassTimer/BasicTimer'
import MobileTimer from '../../components/KlassTimer/MobileTimer'
import { ICourse, IKDTKlass } from '../../../../stores/courses'
import { checkInstallmentAmount, DEFAULT_PLAN } from '../../utils/installment'
import ActionButton from './ActionButton'
import KlassInfoHeader from './KlassInfoHeader'
import KlassInfoSection from './KlassInfoSection'
import { usePreNoticeInfoQuery } from 'app/pre-notice/_queries/usePreNoticeInfoQuery'

dayjs.extend(isBetween)

export interface IApplyPeriodList {
  [status: string]: {
    [date: string]: string
  }
}

interface ICourseInfoProps {
  course: ICourse | IKDTKlass
  isMobile?: boolean
  submitType: {
    [status: string]: {
      disabled: boolean
      name: string
      period?: string
      handler: () => void
    }
  }
  status: string
  isDelayCourse?: boolean
  applyPeriodList?: IApplyPeriodList
  expiryTimestamp?: Date
  isSigned?: boolean
  isSimplifyKDT: boolean
}

interface INextStatus {
  [status: string]: string
}

const CourseInfo: FC<ICourseInfoProps> = ({
  course,
  isMobile,
  submitType,
  status,
  applyPeriodList,
  isDelayCourse,
  expiryTimestamp,
  isSigned,
  isSimplifyKDT,
}) => {
  const {
    id,
    title,
    price,
    shareUrl,
    panelText,
    promotion,
    promotionPrice,
    courseType,
    startedAt,
    applyStartedAt,
    applyEndedAt,
    imageM,
  } = course
  const tooltipContainerRef = useRef(null)
  const curPrice = price.toLocaleString()
  const isOnDemand = useMemo(() => courseType === 'ondemand', [courseType])
  const isWorker = useMemo(() => courseType === 'worker', [courseType])

  const isKDT = useMemo(
    () => courseType === 'kdt' || courseType === 'kdt_plus' || courseType === 'kdt_startup',
    [courseType],
  )

  const [openAddition, setOpenAddition] = useState<string | null>(null)

  const { data: preNoticeInfo } = usePreNoticeInfoQuery()

  const handleItemClick = (itemId: string) => {
    setOpenAddition(itemId === openAddition ? null : itemId)
  }

  const isPromotionActive = useMemo(
    () => promotion !== null && (promotion?.promotionRate > 0 || promotion?.promotionAmount > 0),
    [promotion],
  )
  const promotionRate = useMemo(() => {
    if (promotion === null || promotionPrice === null) return 0
    if (promotion.promotionType === 'rate') return promotion.promotionRate
    else if (promotion.promotionType === 'amount') {
      const rate = promotion.promotionAmount / price
      return Math.floor((rate > 1 ? 1 : rate) * 100)
    }
    return 0
  }, [price, promotion, promotionPrice])
  const amountPerInstallment = useMemo(() => {
    if (isPromotionActive && promotionPrice !== null && promotionPrice > 0)
      return (promotionPrice / 5).toLocaleString()
    else return (price / 5).toLocaleString()
  }, [isPromotionActive, price, promotionPrice])

  const curStatus: INextStatus = isSimplifyKDT
    ? {
        early: 'early',
        assignment: 'assignment',
        assignmentReview: 'assignmentReview',
        participation: dayjs().isBefore(startedAt) ? 'participation' : 'ing',
        participationReview: dayjs().isBefore(startedAt) ? 'participationReview' : 'ing',
        ing: 'ing',
        end: 'end',
      }
    : {
        apply: 'apply',
        assignment: 'assignment',
        participation: dayjs().isBefore(startedAt) ? 'participation' : 'ing',
      }

  const nextStatus: INextStatus = isSimplifyKDT
    ? {
        assignmentReview: 'participation',
        assignment: 'participation',
      }
    : {
        apply: 'assignment',
        assignment: 'participation',
      }

  return (
    <>
      {isMobile ? (
        <div className="block lg:hidden">
          <div className="block lg:hidden">
            <div className="mx-4 pb-6 pt-5">
              <KlassInfoHeader
                isMobile
                klassId={id}
                title={title}
                panelText={panelText}
                shareUrl={shareUrl}
                imgUrl={imageM}
              />
              <div className="flex flex-col">
                {!isKDT && (
                  <>
                    <div className="flex flex-col space-y-1">
                      <div className="flex text-sm text-gray-500">
                        {~~price !== 0 && (
                          <>
                            {isPromotionActive ? (
                              <>
                                <div className="line-through">{curPrice}원</div>
                                {!isOnDemand && (
                                  <span>
                                    &nbsp;/&nbsp;
                                    {promotionPrice !== null && promotionPrice > 0
                                      ? promotionPrice.toLocaleString()
                                      : 0}
                                    원
                                  </span>
                                )}
                              </>
                            ) : (
                              !isOnDemand && <div>{curPrice}원</div>
                            )}
                          </>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-baseline space-x-1">
                          {isPromotionActive ? (
                            <>
                              <span className="text-lg font-bold text-yellow-450">
                                {promotionRate > 0 && promotionRate}%
                              </span>
                              <span className="text-xl font-semibold">
                                {promotionPrice !== null &&
                                promotionPrice > 0 &&
                                checkInstallmentAmount(promotionPrice)
                                  ? `월 ${amountPerInstallment}`
                                  : promotionPrice?.toLocaleString()}
                                원
                              </span>
                            </>
                          ) : (
                            <span className="text-xl font-semibold leading-tight">
                              {~~price === 0
                                ? '무료'
                                : checkInstallmentAmount(price)
                                  ? `월${amountPerInstallment}원`
                                  : `${curPrice}원`}
                            </span>
                          )}
                          {~~price !== 0 &&
                            checkInstallmentAmount(
                              (isPromotionActive ? promotionPrice : price) as number,
                            ) && (
                              <span className="text-sm font-medium text-gray-500">
                                {DEFAULT_PLAN}개월 할부 시
                              </span>
                            )}
                        </div>
                      </div>
                    </div>
                  </>
                )}
                <KlassInfoSection
                  course={course}
                  isKDT={isKDT}
                  isSimplifyKDT={isSimplifyKDT}
                  isDelayCourse={isDelayCourse}
                  isPreNoticeStatus={submitType[status]?.name?.includes('사전알림 신청')}
                  applyStatus={status}
                  applyPeriodList={applyPeriodList}
                />
              </div>
            </div>
          </div>
          {/* 부가 설명 */}
          <div className="mx-4 mb-6 flex flex-col gap-y-2 lg:mt-5">
            {AdditionalList[courseType]?.map(addition => {
              const isOpen = openAddition === addition.id
              return (
                <section
                  className="cursor-pointer rounded-lg bg-gray-50 px-4 py-3"
                  key={addition.id}
                  onClick={() => handleItemClick(addition.id)}
                >
                  <div className="flex items-center justify-between gap-x-2">
                    <div className="flex items-center gap-x-2">
                      <div className="flex h-6 w-6 items-center justify-center">
                        <img src={addition.icon} alt={addition.title} />
                      </div>
                      <p className="text-sm font-bold">
                        {addition.boldTitle}
                        <span className="ml-1 inline-block font-normal">{addition.title}</span>
                      </p>
                    </div>
                    <ChevronUpIcon
                      className={`${isOpen ? '' : 'rotate-180 transform'} w-4 text-gray-400`}
                    />
                  </div>
                  {/* open */}
                  {isOpen && (
                    <div className="mt-4 flex flex-col gap-y-1.5">
                      {addition.summary && (
                        <p className="text-xs font-semibold">{addition.summary}</p>
                      )}
                      <p className="text-xs text-gray-500">
                        <span className="font-semibold">{addition.boldDesc}</span>
                        {addition.desc}
                      </p>
                      {addition.reference && (
                        <span className="text-xs text-gray-500">{addition.reference}</span>
                      )}
                    </div>
                  )}
                </section>
              )
            })}
          </div>
          {/* 하단 고정되는 영역 */}
          <div
            className="fixed bottom-0 left-0 z-30 block w-full shadow-[0px_-1px_4px_0px_rgba(0,0,0,0.05)] lg:hidden"
            style={{ minWidth: 320 }}
          >
            <div className="w-full">
              <div className="flex flex-col">
                {(submitType[status]?.period === 'applying' ||
                  (isWorker && dayjs().isBetween(applyStartedAt, applyEndedAt, undefined, '[)'))) &&
                  expiryTimestamp && <MobileTimer expiryTimestamp={expiryTimestamp} />}
                {!isEmpty(submitType) && !isEmpty(status) && (
                  <>
                    {status === 'early' && (
                      <p className="w-full min-w-0 truncate bg-[#FFF3EB] px-2.5 py-1 text-center text-sm text-primary-normal lg:w-auto lg:bg-transparent lg:px-0 lg:py-0 lg:text-lg lg:font-semibold lg:text-white">
                        {preNoticeInfo?.bannerText}
                      </p>
                    )}
                    <div className="w-full bg-white px-4 py-3">
                      <ActionButton
                        klassId={id}
                        disabled={submitType[isKDT ? curStatus[status] : status]?.disabled}
                        handler={submitType[isKDT ? curStatus[status] : status]?.handler}
                        name={submitType[isKDT ? curStatus[status] : status]?.name}
                        isMobile={isMobile}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div
            className={`${status === 'early' ? 'lg:pb-12' : ''} relative overflow-hidden px-4 pt-5 lg:rounded-lg lg:border lg:border-gray-300 lg:p-6`}
          >
            <KlassInfoHeader
              isMobile={false}
              klassId={id}
              title={title}
              panelText={panelText}
              shareUrl={shareUrl}
              imgUrl={imageM}
            />
            <div className="flex flex-col">
              <KlassInfoSection
                course={course}
                isKDT={isKDT}
                isSimplifyKDT={isSimplifyKDT}
                isDelayCourse={isDelayCourse}
                isPreNoticeStatus={submitType[status]?.name?.includes('사전알림 신청')}
                applyStatus={status}
                applyPeriodList={applyPeriodList}
              />
              {!isKDT && (
                <>
                  <hr className="my-4 border-gray-300" />
                  <div className="flex flex-col space-y-1">
                    <div className="flex text-sm text-gray-500">
                      {~~price !== 0 && (
                        <>
                          {isPromotionActive ? (
                            <>
                              <div className="line-through">{curPrice}원</div>
                              {!isOnDemand && (
                                <span>
                                  &nbsp;/&nbsp;
                                  {promotionPrice !== null && promotionPrice > 0
                                    ? promotionPrice.toLocaleString()
                                    : 0}
                                  원
                                </span>
                              )}
                            </>
                          ) : (
                            !isOnDemand && <div>{curPrice}원</div>
                          )}
                        </>
                      )}
                    </div>
                    <div className="flex items-baseline space-x-1">
                      {isPromotionActive ? (
                        <>
                          <span className="text-lg font-bold text-yellow-450">
                            {promotionRate > 0 && promotionRate}%
                          </span>
                          <span className="text-xl font-semibold">
                            {promotionPrice !== null &&
                            promotionPrice > 0 &&
                            checkInstallmentAmount(promotionPrice)
                              ? `월${amountPerInstallment}`
                              : promotionPrice?.toLocaleString()}
                            원
                          </span>
                        </>
                      ) : (
                        <div className="flex items-baseline space-x-1">
                          <span className="text-xl font-semibold leading-tight">
                            {~~price === 0
                              ? '무료'
                              : checkInstallmentAmount(price)
                                ? `월${amountPerInstallment}원`
                                : `${curPrice}원`}
                          </span>
                        </div>
                      )}
                      {~~price !== 0 &&
                        checkInstallmentAmount(
                          (isPromotionActive ? promotionPrice : price) as number,
                        ) && (
                          <span className="text-sm font-medium text-gray-500">
                            {DEFAULT_PLAN}개월 할부 시
                          </span>
                        )}
                    </div>
                  </div>
                </>
              )}
            </div>
            {/* 결제하기 버튼 */}
            {!isEmpty(submitType) && !isEmpty(status) && (
              <div className={`${status === 'early' ? 'relative mt-9' : ''}`}>
                <ActionButton
                  klassId={id}
                  disabled={submitType[isKDT ? curStatus[status] : status]?.disabled}
                  handler={submitType[isKDT ? curStatus[status] : status]?.handler}
                  name={submitType[isKDT ? curStatus[status] : status]?.name}
                />
              </div>
            )}
            {status === 'early' && (
              <div className="absolute bottom-0 left-0 flex w-full items-center justify-center bg-[#FFF3EB] px-2.5 py-1 text-gray-500">
                <p className="text-sm font-medium text-primary-normal">
                  {preNoticeInfo?.bannerText}
                </p>
              </div>
            )}
          </div>
          {(submitType[status]?.period === 'applying' ||
            (isWorker && dayjs().isBetween(applyStartedAt, applyEndedAt, undefined, '[)'))) &&
            expiryTimestamp && <BasicTimer expiryTimestamp={expiryTimestamp} />}
          {/* 부가 설명 */}
          <div className="flex flex-col gap-y-2 lg:mt-5">
            {AdditionalList[courseType]?.map(addition => {
              const isOpen = openAddition === addition.id
              return (
                <section
                  className="cursor-pointer rounded-lg bg-gray-50 p-4"
                  key={addition.title}
                  onClick={() => handleItemClick(addition.id)}
                >
                  <div className="flex items-center justify-between gap-x-2">
                    <div className="flex items-center gap-x-2">
                      <div className="flex h-6 w-6 items-center justify-center">
                        <img src={addition.icon} alt={addition.title} />
                      </div>
                      <p className="text-sm font-bold">
                        {addition.boldTitle}
                        <span className="ml-1 inline-block font-normal">{addition.title}</span>
                      </p>
                    </div>
                    <ChevronUpIcon
                      className={`${isOpen ? '' : 'rotate-180 transform'} w-4 text-gray-400`}
                    />
                  </div>
                  {/* open */}
                  {isOpen && (
                    <div className="mt-4 flex flex-col gap-y-2">
                      {addition.summary && (
                        <p className="text-sm font-semibold">{addition.summary}</p>
                      )}
                      <p className="text-sm text-gray-500">
                        <span className="font-semibold">{addition.boldDesc}</span>
                        {addition.desc}
                      </p>
                      {addition.reference && (
                        <span className="text-sm text-gray-500">{addition.reference}</span>
                      )}
                    </div>
                  )}
                </section>
              )
            })}
          </div>
        </>
      )}
    </>
  )
}

const levelMap = ['입문', '초급', '중급', '고급', '고급이상']

export default CourseInfo
